/**
 * TypeScript types for AI services GraphQL operations
 */

// Core AI data types
export interface AIUserProfile {
  userId: string;
  skillAssessment: SkillAssessment;
  learningPreferences: LearningPreferences;
  behaviorPatterns: BehaviorPattern[];
  masteryStates: MasteryState[];
  adaptationHistory: AdaptationEvent[];
  recommendationFeedback: RecommendationFeedback[];
}

export interface SkillAssessment {
  skillLevel: SkillLevel;
  focusAreas: string[];
  brewingMethods: string[];
  equipmentOwned: string[];
  timeCommitmentHoursPerWeek: number;
  learningStyle: string;
  preferredContentTypes: string[];
  difficultyPreference: string;
  answers: AssessmentAnswer[];
}

export interface AssessmentAnswer {
  questionId: string;
  answer: string;
  confidence?: number;
}

export interface LearningPreferences {
  durationWeeks: number;
  focusAreas: string[];
  preferredContentTypes: string[];
  difficultyPreference: string;
  learningStyle: string;
  notificationPreferences: Record<string, any>;
}

export interface BehaviorPattern {
  id: string;
  userId: string;
  patternType: string;
  frequency: number;
  confidence: number;
  lastObserved: string;
  metadata: Record<string, any>;
}

export interface MasteryState {
  userId: string;
  journeyId: string;
  skillId: string;
  skillCategory: string;
  masteryProbability: number;
  abilityEstimate: number;
  abilityConfidence: number;
  evidenceCount: number;
  lastUpdated: string;
}

export interface AdaptationEvent {
  id: string;
  userId: string;
  journeyId: string;
  triggerType: AdaptationTrigger;
  decisionType: string;
  confidence: number;
  reasoning: string;
  parameters: Record<string, any>;
  timestamp: string;
}

export interface RecommendationFeedback {
  id: string;
  recommendationId: string;
  userId: string;
  interactionType: 'shown' | 'clicked' | 'completed';
  feedbackScore?: number;
  feedbackText?: string;
  timestamp: string;
}

// Dashboard state types
export interface SmartDashboardState {
  insights: DashboardInsights;
  recommendations: ContentRecommendation[];
  adaptationSuggestions: AdaptationSuggestion[];
  lastUpdated: Date;
  isLoading: boolean;
  error?: string;
}

export interface DashboardInsights {
  nextBestAction: RecommendedAction;
  progressInsights: ProgressInsight[];
  motivationalMessage: string;
  adaptationSuggestions: AdaptationSuggestion[];
}

export interface RecommendedAction {
  type:
    | 'continue_activity'
    | 'start_new_activity'
    | 'review_concept'
    | 'take_break';
  title: string;
  description: string;
  estimatedMinutes: number;
  confidence: number;
  reasoning: string;
}

export interface ProgressInsight {
  type: 'achievement' | 'improvement_area' | 'trend' | 'milestone';
  title: string;
  description: string;
  confidence: number;
  actionable: boolean;
  suggestedActions?: string[];
}

export interface AdaptationSuggestion {
  type:
    | 'difficulty_adjustment'
    | 'pacing_change'
    | 'content_swap'
    | 'focus_shift';
  title: string;
  description: string;
  confidence: number;
  impact: 'low' | 'medium' | 'high';
  reasoning: string;
}

// Recommendation engine types
export interface RecommendationEngineState {
  activeRecommendations: ContentRecommendation[];
  dismissedRecommendations: string[];
  feedbackHistory: RecommendationFeedback[];
  contextualFactors: ContextualFactor[];
  banditsState: BanditsState;
}

export interface ContentRecommendation {
  id: string;
  contentId: string;
  title: string;
  contentType: string;
  difficultyLevel: number;
  estimatedMinutes: number;
  recommendationScore: number;
  recommendationReason: string;
  skillTags: string[];
  qualityScore: number;
  engagementScore: number;
  wasShown: boolean;
  wasClicked: boolean;
  wasCompleted: boolean;
  userFeedback?: number;
  recommendedAt: string;
  shownAt?: string;
  clickedAt?: string;
  completedAt?: string;
}

export interface ContextualFactor {
  type:
    | 'time_of_day'
    | 'device_type'
    | 'location'
    | 'previous_activity'
    | 'mood';
  value: string;
  weight: number;
  confidence: number;
}

export interface BanditsState {
  arms: BanditArm[];
  totalPulls: number;
  lastUpdated: string;
}

export interface BanditArm {
  id: string;
  name: string;
  pulls: number;
  rewards: number;
  averageReward: number;
  confidence: number;
}

// Notification intelligence types
export interface NotificationIntelligenceState {
  optimizationSettings: NotificationOptimization;
  deliveryHistory: NotificationDelivery[];
  engagementPatterns: EngagementPattern[];
  timingPreferences: TimingPreference[];
  contextualTriggers: ContextualTrigger[];
}

export interface NotificationOptimization {
  enabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  maxDailyNotifications: number;
  preferredTimes: string[];
  adaptiveTimingEnabled: boolean;
  contextualTriggersEnabled: boolean;
}

export interface NotificationDelivery {
  id: string;
  userId: string;
  notificationType: string;
  scheduledAt: string;
  deliveredAt?: string;
  openedAt?: string;
  clickedAt?: string;
  dismissed: boolean;
  engagementScore: number;
}

export interface EngagementPattern {
  timeOfDay: string;
  dayOfWeek: string;
  engagementRate: number;
  confidence: number;
  sampleSize: number;
}

export interface TimingPreference {
  userId: string;
  preferredHour: number;
  confidence: number;
  basedOnEngagement: boolean;
  lastUpdated: string;
}

export interface ContextualTrigger {
  type:
    | 'brewing_reminder'
    | 'milestone_achievement'
    | 're_engagement'
    | 'learning_reminder';
  conditions: Record<string, any>;
  enabled: boolean;
  priority: number;
}

// Error handling types
export interface AIErrorRecovery {
  fallbackStrategy: 'rule_based' | 'curated_content' | 'generic_experience';
  userMessage: string;
  retryPolicy: RetryPolicy;
  telemetryData: ErrorTelemetry;
}

export interface RetryPolicy {
  maxAttempts: number;
  backoffStrategy: 'exponential' | 'linear' | 'fixed';
  baseDelay: number;
  maxDelay: number;
}

export interface ErrorTelemetry {
  errorType: string;
  operation: string;
  timestamp: string;
  userAgent?: string;
  networkStatus?: string;
  additionalContext?: Record<string, any>;
}

// Performance monitoring types
export interface AIPerformanceMetrics {
  recommendationLatency: number;
  masteryCalculationTime: number;
  adaptationResponseTime: number;
  cacheHitRate: number;
  offlineCapability: number;
}

// Privacy and security types
export interface AIPrivacySettings {
  personalizationEnabled: boolean;
  dataCollectionLevel: 'minimal' | 'standard' | 'enhanced';
  sharingPreferences: SharingPreference[];
  retentionPeriod: number;
  exportOptions: DataExportOption[];
}

export interface SharingPreference {
  dataType: string;
  shareWithPeers: boolean;
  shareForResearch: boolean;
  anonymized: boolean;
}

export interface DataExportOption {
  format: 'json' | 'csv' | 'pdf';
  includePersonalData: boolean;
  includeAnalytics: boolean;
  includeRecommendations: boolean;
}

// Enums
export enum SkillLevel {
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
  EXPERT = 'EXPERT',
}

export enum AdaptationTrigger {
  PERFORMANCE_DROP = 'PERFORMANCE_DROP',
  ENGAGEMENT_DROP = 'ENGAGEMENT_DROP',
  MASTERY_ACHIEVED = 'MASTERY_ACHIEVED',
  TIME_PRESSURE = 'TIME_PRESSURE',
  DIFFICULTY_MISMATCH = 'DIFFICULTY_MISMATCH',
  CONTENT_PREFERENCE_CHANGE = 'CONTENT_PREFERENCE_CHANGE',
}

export enum JourneyStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// GraphQL operation result types
export interface GraphQLError {
  message: string;
  code: string;
  path?: string[];
  extensions?: Record<string, any>;
}

export interface JourneyResult {
  journey?: any; // Will be replaced by generated types
  error?: GraphQLError;
}

export interface ActivityCompletionResponse {
  progress?: any; // Will be replaced by generated types
  journeyUpdated: boolean;
  weekCompleted: boolean;
  journeyCompleted: boolean;
}

export interface AdaptationResponse {
  journey?: any; // Will be replaced by generated types
  adaptationsApplied: string[];
  activitiesAdded: number;
  activitiesModified: number;
  success: boolean;
  message?: string;
}
